import { createBrowserRouter, type RouteObject } from "react-router";
import { AppShell, RequireAuth } from "@/components/layout/app-shell";
import { NotFoundPage, RouteErrorPage } from "@/features/system/system-pages";

type PageModule = { default: React.ComponentType };
const page = (loader: () => Promise<PageModule>) => async () => ({ Component: (await loader()).default });

const appRoutes: RouteObject[] = [
  { index: true, lazy: page(() => import("@/features/dashboard/dashboard-page")) },
  // Ventes
  { path: "conversations", lazy: page(() => import("@/features/conversations/conversations-page")) },
  { path: "conversations/:conversationId", lazy: page(() => import("@/features/conversations/conversations-page")) },
  { path: "commandes", lazy: page(() => import("@/features/orders/orders-page")) },
  { path: "commandes/nouvelle", lazy: page(() => import("@/features/orders/order-create-page")) },
  { path: "commandes/:orderId", lazy: page(() => import("@/features/orders/order-detail-page")) },
  { path: "clients", lazy: page(() => import("@/features/customers/customers-page")) },
  { path: "clients/:customerId", lazy: page(() => import("@/features/customers/customer-detail-page")) },
  { path: "produits", lazy: page(() => import("@/features/products/products-page")) },
  { path: "produits/nouveau", lazy: page(() => import("@/features/products/product-form-page")) },
  { path: "produits/:productId", lazy: page(() => import("@/features/products/product-form-page")) },
  // Logistique
  { path: "livraisons", lazy: page(() => import("@/features/shipping/shipments-page")) },
  { path: "livraisons/:shipmentId", lazy: page(() => import("@/features/shipping/shipment-detail-page")) },
  { path: "retours", lazy: page(() => import("@/features/returns/returns-page")) },
  { path: "encaissements", lazy: page(() => import("@/features/payments/collections-page")) },
  // Media Buyer
  { path: "media-buyer", lazy: page(() => import("@/features/ads/overview-page")) },
  { path: "media-buyer/campagnes", lazy: page(() => import("@/features/ads/campaigns-page")) },
  { path: "media-buyer/campagnes/nouvelle", lazy: page(() => import("@/features/ads/campaign-wizard-page")) },
  { path: "media-buyer/campagnes/:campaignId", lazy: page(() => import("@/features/ads/campaign-detail-page")) },
  { path: "media-buyer/ensembles", lazy: page(() => import("@/features/ads/adsets-page")) },
  { path: "media-buyer/publicites", lazy: page(() => import("@/features/ads/ads-page")) },
  { path: "media-buyer/creations", lazy: page(() => import("@/features/ads/creatives-page")) },
  { path: "media-buyer/audiences", lazy: page(() => import("@/features/ads/audiences-page")) },
  { path: "media-buyer/performance", lazy: page(() => import("@/features/ads/performance-page")) },
  { path: "media-buyer/experimentations", lazy: page(() => import("@/features/ads/experiments-page")) },
  { path: "media-buyer/alertes", lazy: page(() => import("@/features/ads/alerts-page")) },
  { path: "media-buyer/ia", lazy: page(() => import("@/features/ads/ai-media-buyer-page")) },
  { path: "media-buyer/recommandations", lazy: page(() => import("@/features/ads/recommendations-page")) },
  { path: "media-buyer/decisions", lazy: page(() => import("@/features/ads/decisions-page")) },
  { path: "media-buyer/profil-business", lazy: page(() => import("@/features/ads/business-profile-page")) },
  { path: "media-buyer/garde-fous", lazy: page(() => import("@/features/ads/guardrails-page")) },
  { path: "media-buyer/comptes", lazy: page(() => import("@/features/ads/ad-accounts-page")) },
  // Intelligence IA
  { path: "ia/insights", lazy: page(() => import("@/features/ai/insights-page")) },
  { path: "ia/business-brain", lazy: page(() => import("@/features/ai/business-brain-page")) },
  // Croissance
  { path: "relances", lazy: page(() => import("@/features/automation/followups-page")) },
  { path: "playbooks", lazy: page(() => import("@/features/automation/playbooks-page")) },
  { path: "playbooks/:playbookId", lazy: page(() => import("@/features/automation/playbook-editor-page")) },
  { path: "marches", lazy: page(() => import("@/features/markets/markets-page")) },
  // Site & landing
  { path: "site", lazy: page(() => import("@/features/sites/sites-page")) },
  { path: "site/:siteId", lazy: page(() => import("@/features/sites/site-detail-page")) },
  { path: "modeles", lazy: page(() => import("@/features/sites/templates-page")) },
  { path: "formulaires", lazy: page(() => import("@/features/sites/forms-page")) },
  { path: "formulaires/:formId", lazy: page(() => import("@/features/sites/form-detail-page")) },
  { path: "domaines", lazy: page(() => import("@/features/sites/domains-page")) },
  { path: "pixel-suivi", lazy: page(() => import("@/features/sites/tracking-page")) },
  { path: "seo", lazy: page(() => import("@/features/sites/seo-page")) },
  // Analyse
  { path: "analyse", lazy: page(() => import("@/features/analytics/analysis-page")) },
  { path: "risque", lazy: page(() => import("@/features/analytics/risk-page")) },
  { path: "rapports", lazy: page(() => import("@/features/analytics/reports-page")) },
  // Paramètres
  { path: "integrations", lazy: page(() => import("@/features/integrations/integrations-page")) },
  { path: "integrations/:provider", lazy: page(() => import("@/features/integrations/integration-detail-page")) },
  { path: "organisation", lazy: page(() => import("@/features/settings/organization-page")) },
  { path: "abonnement", lazy: page(() => import("@/features/settings/billing-page")) },
  { path: "reglages", lazy: page(() => import("@/features/settings/account-settings-page")) },
  { path: "audit", lazy: page(() => import("@/features/settings/audit-page")) },
  { path: "admin", lazy: page(() => import("@/features/admin/admin-page")) },
  { path: "*", Component: NotFoundPage },
];

export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: "/connexion", lazy: page(() => import("@/features/auth/login-page")) },
      { path: "/inscription", lazy: page(() => import("@/features/auth/signup-page")) },
      { path: "/mot-de-passe-oublie", lazy: page(() => import("@/features/auth/forgot-password-page")) },
      { path: "/reinitialiser-mot-de-passe", lazy: page(() => import("@/features/auth/reset-password-page")) },
      { path: "/invitation/:token", lazy: page(() => import("@/features/auth/invitation-page")) },
      { path: "/verifier-email", lazy: page(() => import("@/features/auth/verify-email-page")) },
      { path: "/suivi/:code", lazy: page(() => import("@/features/shipping/public-tracking-page")) },
      {
        path: "/site/:siteId/editeur/:pageId",
        lazy: async () => {
          const mod = await import("@/features/sites/page-editor-page");
          const Editor = mod.default;
          return {
            Component: () => (
              <RequireAuth>
                <Editor />
              </RequireAuth>
            ),
          };
        },
      },
      { path: "/", element: <AppShell />, children: appRoutes },
    ],
  },
]);
